import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import Logo from "./Logo";
import BookCall from "./BookCall";
import { trackEvent } from "@/lib/tracking";

const SERVICE_LINKS = [
  { to: "/marketo-services", label: "Adobe Marketo Engage" },
  { to: "/services/salesforce-crm", label: "Salesforce & CRM" },
  { to: "/services/campaign-operations", label: "Campaign Operations" },
  { to: "/services/managed-services", label: "Managed Services" },
  { to: "/services/martech-integration", label: "Martech & Data" },
  { to: "/services/reporting-attribution", label: "Reporting & Attribution" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const { pathname } = useLocation();
  const dropRef = useRef(null);
  const lastY = useRef(0);

  // Hide the in-nav "Explore Services" and "Book a Meeting" CTAs on desktop when
  // the page already shows the same CTAs in-page. This covers every marketing
  // route (home, services, all service detail pages, marketo, case studies,
  // about, contact, resources, etc.). They remain available on legal/404
  // routes where there is no in-page CTA, and via the mobile menu.
  const PAGES_WITHOUT_INPAGE_CTA = [
    /^\/privacy/,
    /^\/cookie/,
    /^\/terms/,
    /^\/legal/,
  ];
  const hasInpageCta = !PAGES_WITHOUT_INPAGE_CTA.some((rx) => rx.test(pathname));
  // Hide nav CTAs on every page with an in-page CTA, plus on /contact itself.
  const hideCta = hasInpageCta || pathname === "/contact" || pathname.startsWith("/contact/");

  useEffect(() => { setOpen(false); setSvcOpen(false); }, [pathname]);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 12);
      // Scroll-direction aware hide: hide when scrolling down past 120px, show when scrolling up
      if (y > 120 && y > lastY.current + 4) {
        setHidden(true);
      } else if (y < lastY.current - 4 || y <= 80) {
        setHidden(false);
      }
      lastY.current = y;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const onClick = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setSvcOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      data-hidden={hidden}
      className={`sticky top-0 z-40 transition-all duration-300 will-change-transform ${
        hidden && !open ? "-translate-y-full" : "translate-y-0"
      } ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-[#E5E7EB]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" aria-label="Xadentra home">
          <Logo variant="light" />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          <div ref={dropRef} className="relative">
            <button
              data-testid="nav-services-toggle"
              onClick={() => setSvcOpen((s) => !s)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[#07111F] hover:text-[#6D4DFF] transition-colors"
            >
              Services <ChevronDown className={`w-4 h-4 transition-transform ${svcOpen ? "rotate-180" : ""}`} />
            </button>
            {svcOpen && (
              <div
                data-testid="nav-services-dropdown"
                className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[420px] bg-white rounded-xl border border-[#E5E7EB] shadow-[0_24px_60px_rgba(7,17,31,0.12)] p-3"
              >
                <div className="grid grid-cols-1 gap-1">
                  <Link to="/services" className="px-3 py-2 text-xs uppercase tracking-widest text-[#6B7280] font-mono">All services →</Link>
                  {SERVICE_LINKS.map((s) => (
                    <Link
                      key={s.to}
                      to={s.to}
                      data-testid={`nav-service-${s.to.split("/").pop()}`}
                      className="px-3 py-2.5 rounded-lg text-sm text-[#07111F] hover:bg-[#F7F9FC] hover:text-[#6D4DFF] transition-colors"
                    >
                      {s.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {[
            { to: "/case-studies", label: "Case Studies" },
            { to: "/work", label: "Our Work" },
            { to: "/resources", label: "Resources" },
            { to: "/about", label: "About" },
            { to: "/contact", label: "Contact" },
          ].map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive ? "text-[#6D4DFF]" : "text-[#07111F] hover:text-[#6D4DFF]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {!hideCta && (
            <>
              <Link
                to="/services"
                data-testid="nav-secondary-cta"
                className="text-sm text-[#07111F] hover:text-[#6D4DFF]"
                onClick={() => trackEvent("cta_click", { id: "nav_explore_services" })}
              >
                Explore Services
              </Link>
              <BookCall source="nav_book_call" className="text-sm">
                Book a Meeting
              </BookCall>
            </>
          )}
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 text-[#07111F]"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-[#E5E7EB]">
          <div className="px-6 py-6 flex flex-col gap-1">
            <details className="group">
              <summary className="py-3 text-base font-medium text-[#07111F] cursor-pointer list-none flex items-center justify-between" data-testid="mobile-nav-services">
                Services <ChevronDown className="w-4 h-4 group-open:rotate-180 transition-transform" />
              </summary>
              <div className="pl-3 pb-3 flex flex-col gap-1">
                {SERVICE_LINKS.map((s) => (
                  <Link key={s.to} to={s.to} className="py-2 text-sm text-[#07111F]">{s.label}</Link>
                ))}
              </div>
            </details>
            <Link to="/case-studies" data-testid="mobile-nav-case-studies" className="py-3 text-base text-[#07111F]">Case Studies</Link>
            <Link to="/work" data-testid="mobile-nav-work" className="py-3 text-base text-[#07111F]">Our Work</Link>
            <Link to="/resources" data-testid="mobile-nav-resources" className="py-3 text-base text-[#07111F]">Resources</Link>
            <Link to="/about" data-testid="mobile-nav-about" className="py-3 text-base text-[#07111F]">About</Link>
            <Link to="/contact" data-testid="mobile-nav-contact" className="py-3 text-base text-[#07111F]">Contact</Link>
            {!hideCta && (
              <BookCall source="mobile_nav_book_call" className="mt-3 justify-center text-sm">
                Book a Meeting
              </BookCall>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
